import moment from "moment";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  useGetIsLegalEntity,
  useGetOptionsApprovalStatus,
} from "~/v2/api/hooks";
import type { Account, OptionsApprovalStatus } from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { isPaperOrOnboarding } from "~/v2/utils";
import { timer } from "../entityOnboarding/timer";

export const isLocalStorageAvailable = () => {
  try {
    // do not optional chain localStorage here, allow error to be caught
    window.localStorage.setItem("_test_", "1");
    window.localStorage.removeItem("_test_");
    return true;
  } catch (_error) {
    return false;
  }
};

const TIER_LOCAL_STORAGE_KEY = "options_opt_in_tier";
const TIMER_LOCAL_STORAGE_KEY = "opt_in_timer";
const UTM_LOCAL_STORAGE_KEY = "options_opt_in_utms";
export const OPTIONS_APPROVED_STATUSES = ["APPROVED", "LOWER_LEVEL_APPROVED"];

/** Local Storage Helper */
export const storage = {
  setUTMs: (utms: { [key: string]: string }) => {
    window.localStorage?.setItem(UTM_LOCAL_STORAGE_KEY, JSON.stringify(utms));
  },
  getUTMs: (): { [key: string]: string } | undefined => {
    const utms = window.localStorage?.getItem(UTM_LOCAL_STORAGE_KEY);

    if (!utms) {
      return;
    }

    return JSON.parse(utms) as { [key: string]: string };
  },
  removeUTMs: () => {
    window.localStorage?.removeItem(UTM_LOCAL_STORAGE_KEY);
  },
  setTier: (tier: string) => {
    window.localStorage?.setItem(TIER_LOCAL_STORAGE_KEY, tier);
  },
  getTier: (): string | null => {
    return window.localStorage?.getItem(TIER_LOCAL_STORAGE_KEY);
  },
  removeTier: () => {
    window.localStorage?.removeItem(TIER_LOCAL_STORAGE_KEY);
  },
  startTimer: () => {
    if (!timer.isSet(TIMER_LOCAL_STORAGE_KEY)) {
      timer.start(TIMER_LOCAL_STORAGE_KEY);
    }
  },
  endTimer: () => {
    const duration = timer.end(TIMER_LOCAL_STORAGE_KEY);
    timer.remove(TIMER_LOCAL_STORAGE_KEY);
    return duration;
  },
};

export const logAmplitudeEvent = {
  started: () => {
    AmplitudeProvider.dispatch("algodash_options_opt_in_started");
  },
  completed: (duration?: number, utms?: { [key: string]: string }) => {
    AmplitudeProvider.dispatch("algodash_options_opt_in_completed", {
      duration,
      ...utms,
    });
  },
  nextClicked: () => {
    AmplitudeProvider.dispatch("algodash_options_opt_in_next_clicked", {
      url: window.location.pathname,
    });
  },
  backClicked: () => {
    AmplitudeProvider.dispatch("algodash_options_opt_in_back_clicked", {
      url: window.location.pathname,
    });
  },
  pageCompleted: (duration?: number) => {
    AmplitudeProvider.dispatch("algodash_options_opt_in_page_completed", {
      duration,
      url: window.location.pathname,
    });
  },
};

const getEffectiveOptionLevel = (account?: Account) => {
  return Math.min(
    account?.admin_configurations?.max_options_trading_level ?? 3,
    account?.user_configurations?.max_options_trading_level ?? 3,
    account?.options_approved_level ?? 0,
  );
};

const isOptionsOnboardingAvailable = (
  account?: Account,
  isLegalEntity?: boolean,
  optionsApprovalStatus?: OptionsApprovalStatus,
  allowLegalEntities?: boolean,
) => {
  if (!account || !optionsApprovalStatus) {
    return false;
  }

  // Do not allow user to onboard for options:
  // if user is paper only or onboarding
  // Or options_approved_level is 3 (highest level)
  // Or is legal entity and allowLegalEntities is false
  if (
    isPaperOrOnboarding(account) ||
    account.options_approved_level === 3 ||
    (isLegalEntity && !allowLegalEntities)
  ) {
    return false;
  }

  // If user's application is in pending status don't allow them to apply
  if (["PENDING"].includes(optionsApprovalStatus?.OptionsRequestStatus ?? "")) {
    return false;
  }

  // Don't allow the user to reapply if their reapply available date is after today
  if (
    optionsApprovalStatus?.ReapplyAvailableDate &&
    moment(optionsApprovalStatus?.ReapplyAvailableDate).isAfter(new Date())
  ) {
    return false;
  }

  return true;
};

const isOptionsApproved = (
  account?: Account,
  optionsApprovalStatus?: OptionsApprovalStatus,
) =>
  OPTIONS_APPROVED_STATUSES.includes(
    optionsApprovalStatus?.OptionsRequestStatus ?? "",
  ) || (account?.options_approved_level ?? 0) > 0;

export const useIsOptionsOnboardingAvailable = (
  { allowLegalEntities }: { allowLegalEntities?: boolean } = {
    allowLegalEntities: false,
  },
): {
  isLoading: boolean;
  isOptionsOnboardingAvailable: boolean | undefined;
  isOptionsApproved: boolean | undefined;
  optionsApprovalStatus: OptionsApprovalStatus | undefined;
  optionsApprovedLevel: number;
  effectiveOptionsLevel: number;
} => {
  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();
  const {
    product,
    currentAccount,
    isLoading: isCurrentAccountLoading,
  } = useAccountContext();
  const isEnabled = product === "live";

  const { data: optionsApprovalStatus, isLoading: isApprovalLoading } =
    useGetOptionsApprovalStatus({
      accountID: currentAccount?.id,
      options: { enabled: isEnabled && !!currentAccount?.id },
    });

  const isLoading =
    isLegalEntityLoading || isCurrentAccountLoading || isApprovalLoading;

  if (isLoading) {
    return {
      isLoading,
      isOptionsOnboardingAvailable: undefined,
      isOptionsApproved: undefined,
      optionsApprovalStatus: undefined,
      optionsApprovedLevel: 0,
      effectiveOptionsLevel: 0,
    };
  }

  if (product === "paper") {
    return {
      isLoading: false,
      isOptionsApproved: isOptionsApproved(currentAccount),
      isOptionsOnboardingAvailable: false,
      optionsApprovalStatus: undefined,
      optionsApprovedLevel: currentAccount?.options_approved_level ?? 0,
      effectiveOptionsLevel: getEffectiveOptionLevel(currentAccount),
    };
  }

  return {
    isLoading: false,
    isOptionsApproved: isOptionsApproved(currentAccount, optionsApprovalStatus),
    isOptionsOnboardingAvailable:
      isEnabled &&
      isOptionsOnboardingAvailable(
        currentAccount,
        isLegalEntity,
        optionsApprovalStatus,
        allowLegalEntities,
      ),
    optionsApprovalStatus,
    optionsApprovedLevel: currentAccount?.options_approved_level ?? 0,
    effectiveOptionsLevel: getEffectiveOptionLevel(currentAccount),
  };
};

export const getUTMsFromSearch = (
  search: string,
): { [key: string]: string } | undefined => {
  if (!search) {
    return;
  }

  const params = new URLSearchParams(search);

  const source = params.get("utm_source");
  const medium = params.get("utm_medium");
  const campaign = params.get("utm_campaign");
  const term = params.get("utm_term");
  const content = params.get("utm_content");

  const utms = {
    ...(source && { utm_source: source }),
    ...(medium && { utm_medium: medium }),
    ...(campaign && { utm_campaign: campaign }),
    ...(term && { utm_term: term }),
    ...(content && { utm_content: content }),
  };

  if (!Object.keys(utms).length) {
    return;
  }

  return utms;
};
